/**
 * Selection logic for the "view / location" feature chips surfaced on the WLS
 * listing detail header (see DIR-12297).
 *
 * Kept free of any React/i18n dependency so it can be unit tested in isolation
 * (see spec/javascript/wls-location-features.spec.js). The component
 * `LocationFeatureChips` maps the returned keys to translated labels.
 *
 * The data lives in `listing.property.features_location`, an object keyed by
 * feature key (e.g. `LOCATION_TYPE_MOUNTAIN_VIEW`) whose truthy `.value` marks
 * the feature as set — the same shape consumed by `AmenitiesListGroup`.
 */

// Config-driven priority list. Ordering here is authoritative and independent
// of the (unordered) stored `features_location` object, so chips render
// deterministically. Waterfront/frontage and view features are prioritised
// over generic location descriptors.
const LOCATION_FEATURE_PRIORITY = [
  'LOCATION_TYPE_WATERFRONT',
  'LOCATION_TYPE_BEACH_FRONT',
  'LOCATION_TYPE_OCEAN_FRONT',
  'LOCATION_TYPE_LAKE_FRONT',
  'LOCATION_TYPE_GOLF_COURSE_FRONT',
  'LOCATION_TYPE_SKI_IN',
  'LOCATION_TYPE_SKI_OUT',
  'LOCATION_TYPE_OCEAN_VIEW',
  'LOCATION_TYPE_LAKE_VIEW',
  'LOCATION_TYPE_MOUNTAIN_VIEW',
  'LOCATION_TYPE_BEACH_VIEW',
  'LOCATION_TYPE_WATER_VIEW',
  'LOCATION_TYPE_GOLF_COURSE_VIEW',
  'LOCATION_TYPE_MONUMENT_VIEW',
  'LOCATION_TYPE_NEAR_OCEAN',
  'LOCATION_TYPE_BEACH',
  'LOCATION_TYPE_LAKE',
  'LOCATION_TYPE_MOUNTAIN',
  'LOCATION_TYPE_RIVER',
  'LOCATION_TYPE_RESORT',
  'LOCATION_TYPE_DOWNTOWN',
  'LOCATION_TYPE_TOWN',
  'LOCATION_TYPE_VILLAGE',
  'LOCATION_TYPE_RURAL'
];

// Default maximum number of chips to surface beside beds/baths/guests.
const DEFAULT_LOCATION_FEATURE_LIMIT = 3;

/**
 * Return the ordered, capped list of location feature keys that are set for a
 * listing, ready to render as chips.
 *
 * @param {Object} featuresLocation `listing.property.features_location`
 * @param {Object} [options]
 * @param {number} [options.limit] max chips to return (defaults to 3)
 * @returns {string[]} feature keys in deterministic priority order
 */
const getFeaturedLocationFeatures = (featuresLocation, options = {}) => {
  if (!featuresLocation || typeof featuresLocation !== 'object') return [];

  const limit =
    typeof options.limit === 'number' && options.limit >= 0
      ? options.limit
      : DEFAULT_LOCATION_FEATURE_LIMIT;

  return LOCATION_FEATURE_PRIORITY.filter(key => {
    const feature = featuresLocation[key];
    return Boolean(feature && feature.value);
  }).slice(0, limit);
};

module.exports = getFeaturedLocationFeatures;
module.exports.getFeaturedLocationFeatures = getFeaturedLocationFeatures;
module.exports.LOCATION_FEATURE_PRIORITY = LOCATION_FEATURE_PRIORITY;
module.exports.DEFAULT_LOCATION_FEATURE_LIMIT = DEFAULT_LOCATION_FEATURE_LIMIT;
